import { Box, Typography, useTheme } from "@mui/material";
import axios from "axios";

/** Standalone axios instance without global interceptors (no Authorization header).
 *  S3 presigned URLs return 400 if an Authorization header is sent alongside
 *  query-string signature params. */
export const plainHttp = axios.create();

export interface TranscriptMessage {
  time: string;
  speaker: string;
  rawSpeaker?: string;
  text: string;
}

/** Parse Zoom native JSON transcript.
 *  Supports: { diarization: { timeline: [...] } }, { timeline: [...] }, or { diarization: [...] } */
export const parseZoomJsonTranscript = (data: any): TranscriptMessage[] | null => {
  // Resolve the timeline array — check diarization first, then top-level
  const timeline =
    data?.diarization?.timeline ?? data?.diarization ?? data?.timeline;
  if (!Array.isArray(timeline)) return null;

  const messages: TranscriptMessage[] = [];
  for (const entry of timeline) {
    const text = entry.text || entry.content || entry.message;
    if (!text) continue;

    // Resolve speaker — try common field names. Preserve the raw label so
    // analysis-driven speaker_mapping can resolve it to Agent/Client later.
    const rawSpeaker =
      entry.zoom_userid ||
      entry.users?.[0]?.zoom_userid ||
      entry.username ||
      entry.speaker_name ||
      entry.speaker ||
      entry.user ||
      null;
    const isUnknown =
      !rawSpeaker ||
      rawSpeaker === "Unknown Speaker" ||
      entry.zoom_userid === "Unknown Speaker" ||
      entry.users?.[0]?.zoom_userid === "Unknown Speaker";
    const speaker = isUnknown ? "Customer" : rawSpeaker;

    // Resolve timestamp — try common field names
    const rawTime =
      entry.ts ||
      entry.start_time ||
      entry.start ||
      entry.time ||
      entry.timestamp ||
      "";
    const time = String(rawTime).split(".")[0];

    messages.push({ time, speaker, rawSpeaker: rawSpeaker ?? undefined, text });
  }
  return messages.length > 0 ? messages : null;
};

export const parseTranscription = (raw: string): TranscriptMessage[] | null => {
  // Try parsing as JSON first (Zoom native transcript format)
  try {
    const json = JSON.parse(raw);
    if (
      json?.type === "zoom_transcript" ||
      json?.timeline ||
      json?.diarization
    ) {
      const result = parseZoomJsonTranscript(json);
      if (result) return result;
    }
  } catch {
    // Not JSON — continue with text-based parsing
  }

  const lines = raw.trim().split("\n");
  const messages: TranscriptMessage[] = [];

  // Try format: [HH:MM:SS] Speaker: message
  const bracketRegex = /^\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*(.+?):\s*(.+)$/;
  for (const line of lines) {
    const match = line.trim().match(bracketRegex);
    if (match) {
      messages.push({
        time: match[1],
        speaker: match[2],
        rawSpeaker: match[2],
        text: match[3],
      });
    }
  }
  if (messages.length > 0) return messages;

  // Try WebVTT-like format
  const vttTimestampRegex =
    /^(\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?)\s*-->\s*\d{1,2}:\d{2}(?::\d{2})?(?:\.\d+)?$/;
  let i = 0;
  while (i < lines.length) {
    const trimmed = lines[i].trim();

    if (
      !trimmed ||
      trimmed === "WEBVTT" ||
      trimmed === "NOTE" ||
      /^\d+$/.test(trimmed)
    ) {
      i++;
      continue;
    }

    const tsMatch = trimmed.match(vttTimestampRegex);
    if (tsMatch) {
      const time = tsMatch[1];
      i++;
      while (i < lines.length && !lines[i].trim()) i++;
      if (i >= lines.length) break;

      const speakerOrText = lines[i].trim();
      i++;

      const textParts: string[] = [];
      while (i < lines.length) {
        const next = lines[i].trim();
        if (!next || vttTimestampRegex.test(next) || /^\d+$/.test(next)) break;
        textParts.push(next);
        i++;
      }

      if (textParts.length > 0) {
        const sp = speakerOrText.replace(/:$/, "");
        messages.push({
          time,
          speaker: sp,
          rawSpeaker: sp,
          text: textParts.join(" "),
        });
      } else {
        messages.push({ time, speaker: "Speaker", text: speakerOrText });
      }
      continue;
    }

    i++;
  }
  if (messages.length > 0) return messages;

  // Try simple "Speaker: message" per line
  const simpleRegex = /^(.+?):\s+(.+)$/;
  for (const line of lines) {
    const match = line.trim().match(simpleRegex);
    if (match) {
      messages.push({
        time: "",
        speaker: match[1],
        rawSpeaker: match[1],
        text: match[2],
      });
    }
  }
  if (messages.length > 0) return messages;

  return null;
};

/** Format transcript time to "M:SS" or "H:MM:SS".
 *  Handles: seconds as number/string (e.g. 65 -> "1:05"), "0:05:23" -> "5:23", "0:01" kept as-is */
export const formatTranscriptTime = (time: string): string => {
  // If it's a plain number (seconds), convert to M:SS
  const num = Number(time);
  if (!isNaN(num) && !time.includes(":")) {
    const totalSec = Math.floor(num);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  const parts = time.split(":");
  if (parts.length === 3) {
    // H:MM:SS — drop leading hour if 0
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const s = parts[2].split(".")[0]; // strip ms
    if (h === 0) return `${m}:${s.padStart(2, "0")}`;
    return `${h}:${parts[1]}:${s.padStart(2, "0")}`;
  }
  // M:SS — keep as-is
  return time.split(".")[0];
};

/** Parse a transcript time string into seconds.
 *  Accepts: plain number ("65"), "M:SS", "H:MM:SS", with optional ".ms" suffix. */
export const parseTimeToSeconds = (time: string): number => {
  if (!time) return 0;
  const num = Number(time);
  if (!isNaN(num) && !time.includes(":")) return num;

  const [mainPart] = time.split(".");
  const parts = mainPart.split(":").map((p) => parseInt(p, 10));
  if (parts.some(isNaN)) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
};

/** Single transcript row — Figma: 14px, flex-wrap, gap 4px/8px */
export const TranscriptRow = ({
  msg,
  isActive,
  onClick,
  rowRef,
}: {
  msg: TranscriptMessage;
  isActive?: boolean;
  onClick?: () => void;
  rowRef?: (el: HTMLDivElement | null) => void;
}) => {
  const theme = useTheme();

  return (
    <Box
      ref={rowRef}
      onClick={onClick}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "4px 8px",
        alignItems: "flex-start",
        p: "8px",
        borderRadius: theme.nawy.radius.sm,
        cursor: onClick ? "pointer" : "default",
        backgroundColor: isActive
          ? theme.nawy.colors.primitives.blue[50]
          : "transparent",
        transition: "background-color 0.15s",
        "&:hover": onClick
          ? { backgroundColor: theme.nawy.colors.semantic.layer.secondary }
          : undefined,
      }}
    >
      {/* Timestamp */}
      {msg.time && (
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: theme.nawy.colors.semantic.text.secondary,
            flexShrink: 0,
            width: 32,
          }}
        >
          {formatTranscriptTime(msg.time)}
        </Typography>
      )}

      {/* Speaker name (bold) */}
      <Typography
        sx={{
          ...theme.nawy.typography.english.variants.body.md.semibold,
          color: theme.nawy.colors.semantic.text.primary,
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        {msg.speaker}
      </Typography>

      {/* Message text */}
      <Typography
        sx={{
          ...theme.nawy.typography.english.variants.body.md.regular,
          color: theme.nawy.colors.semantic.text.primary,
          wordBreak: "break-word",
          flex: "1 0 0",
          minWidth: 0,
        }}
      >
        {msg.text}
      </Typography>
    </Box>
  );
};

export const KEY_POINTS_PREVIEW = 3;
